import React, { useState, useEffect } from 'react';
import { Modal, Badge, Card, Collapse, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, 
  faTimes, 
  faUser, 
  faCalendarAlt,
  faChevronDown,
  faChevronRight,
  faExclamationTriangle,
  faPlusCircle,
  faEdit,
  faTrashAlt,
  faEye,
  faDownload,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useOrderHistory, useAssignmentHistory } from '../../hooks/useLogging';

/**
 * 📋 Modal para mostrar historial de entidades
 * Muestra timeline de cambios con detalles expandibles
 */
const HistoryModal = ({ 
  show, 
  onHide, 
  entityType, 
  entityId, 
  entityName = 'Entidad' 
}) => {
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  // Usar el hook apropiado según el tipo de entidad
  const orderHistory = useOrderHistory(entityType === 'INSERTION_ORDER' ? entityId : null);
  const assignmentHistory = useAssignmentHistory(entityType === 'BUDGET_ASSIGNMENT' ? entityId : null);

  // Seleccionar los datos apropiados
  const { history, loading, error, refetch } = entityType === 'INSERTION_ORDER' 
    ? orderHistory 
    : assignmentHistory;

  // Debug: Log detallado para identificar el problema
  useEffect(() => {
    // Debug logs removed for production
  }, [entityType, entityId, orderHistory, assignmentHistory, history]);

  // Forzar recarga cuando se abre el modal
  useEffect(() => {
    if (show && entityId && !loading && !history) {
      refetch();
    }
  }, [show, entityId, loading, history, refetch]);

  const toggleLogExpansion = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getActionBadgeVariant = (action) => {
    const variants = {
      'CREATE': 'success',
      'UPDATE': 'warning', 
      'DELETE': 'danger',
      'VIEW': 'info',
      'EXPORT': 'primary'
    };
    return variants[action] || 'secondary';
  };

  const getActionIcon = (action) => {
    const icons = {
      'CREATE': faPlusCircle,
      'UPDATE': faEdit,
      'DELETE': faTrashAlt, 
      'VIEW': faEye,
      'EXPORT': faDownload
    };
    return icons[action] || faInfoCircle;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const logTime = new Date(timestamp);
    const diffMs = now - logTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    
    return logTime.toLocaleDateString('es-ES');
  };

  const formatValue = (field, value) => {
    if (value === null || value === undefined) return 'N/A';

    // Campos monetarios
    if (['grossInvestment', 'netInvestment', 'brokerFee', 'mediaFee', 'adminFee', 'montoValor'].includes(field)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(value);
    }

    // Fechas
    if (['dateFrom', 'dateTo', 'createdAt', 'updatedAt'].includes(field)) {
      return new Date(value).toLocaleDateString('es-ES');
    }

    // Booleanos
    if (field === 'isActive') {
      return value ? 'Activo' : 'Inactivo';
    }

    return String(value);
  };

  const getFieldLabel = (field) => {
    const labels = {
      'clientName': 'Cliente',
      'businessUnit': 'Unidad de Negocio',
      'grossInvestment': 'Inversión Bruta',
      'netInvestment': 'Inversión Neta',
      'brokerFee': 'Fee Broker',
      'mediaFee': 'Fee Media',
      'adminFee': 'Fee Administrativo',
      'dateFrom': 'Fecha Inicio',
      'dateTo': 'Fecha Fin',
      'currency': 'Moneda',
      'description': 'Descripción',
      'montoValor': 'Monto',
      'isActive': 'Estado'
    };
    return labels[field] || field;
  };

  const renderChanges = (log) => {
    if (!log.changedFields || log.changedFields.length === 0) {
      // Para CREATE, mostrar los valores nuevos como información
      if (log.action === 'CREATE' && log.newValues) {
        return (
          <div className="creation-info">
            <h6 className="text-success mb-2">📋 Datos de Creación:</h6>
            <div className="row">
              {Object.entries(log.newValues).slice(0, 8).map(([field, value]) => (
                <div key={field} className="col-md-6 mb-2">
                  <div className="p-2 bg-light rounded">
                    <small className="text-muted d-block">{getFieldLabel(field)}:</small>
                    <span className="fw-semibold">{formatValue(field, value)}</span>
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(log.newValues).length > 8 && (
              <small className="text-muted">
                y {Object.keys(log.newValues).length - 8} campos más...
              </small>
            )}
          </div>
        );
      }
      
      return <p className="text-muted mb-0">No hay detalles de cambios disponibles</p>;
    }

    return (
      <div className="changes-list">
        {log.changedFields.map((field, index) => (
          <div key={index} className="change-item mb-2 p-2 bg-light rounded">
            <strong className="d-block mb-1">{getFieldLabel(field)}</strong>
            <div className="d-flex align-items-center">
              <div className="flex-fill">
                <small className="text-muted d-block">Anterior:</small>
                <span className="text-danger">
                  {formatValue(field, log.oldValues[field])}
                </span>
              </div>
              <div className="mx-2">
                <FontAwesomeIcon icon={faChevronRight} className="text-muted" />
              </div>
              <div className="flex-fill">
                <small className="text-muted d-block">Nuevo:</small>
                <span className="text-success">
                  {formatValue(field, log.newValues[field])}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Verificar si tenemos datos para mostrar
  const hasData = history && history.logs && history.logs.length > 0;
  const hasError = error && !loading;
  const isEmpty = !loading && !error && show && (!history || !history.logs || history.logs.length === 0);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faHistory} className="me-2" />
          Historial de {entityName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando historial...</span>
            </Spinner>
            <p className="mt-2 text-muted">Cargando historial...</p>
          </div>
        )}

        {hasError && (
          <Alert variant="danger" className="d-flex align-items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        )}

        {isEmpty && (
          <Alert variant="info">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            No se encontraron registros de historial para esta entidad.
          </Alert>
        )}

        {hasData && (
          <div className="timeline">
            <div className="mb-3">
              <h6 className="text-muted">
                Total de registros: <Badge bg="primary">{history.totalLogs}</Badge>
              </h6>
            </div>

            {history.logs.map((log, index) => (
              <Card key={log.id} className="mb-3 timeline-item">
                <Card.Body>
                  <div className="d-flex align-items-start">
                    <div className="timeline-marker me-3">
                      <Badge 
                        bg={getActionBadgeVariant(log.action)} 
                        className="rounded-circle p-2"
                      >
                        <FontAwesomeIcon icon={getActionIcon(log.action)} />
                      </Badge>
                    </div>

                    <div className="flex-fill">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">
                            <Badge bg={getActionBadgeVariant(log.action)} className="me-2">
                              {log.action}
                            </Badge>
                            {log.description}
                          </h6>
                          <div className="text-muted small">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            {log.userName || 'Usuario desconocido'} 
                            {log.userEmail && ` (${log.userEmail})`}
                            <span className="mx-2">•</span>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                            {formatTimeAgo(log.createdAt)}
                          </div>
                        </div>
                        
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => toggleLogExpansion(log.id)}
                        >
                          <FontAwesomeIcon 
                            icon={expandedLogs.has(log.id) ? faChevronDown : faChevronRight} 
                          />
                          <span className="ms-1">
                            {expandedLogs.has(log.id) ? 'Ocultar' : 'Ver'} detalles
                          </span>
                        </button>
                      </div>

                      <Collapse in={expandedLogs.has(log.id)}>
                        <div>
                          <hr />
                          <h6 className="mb-2">
                            {log.action === 'CREATE' ? 'Información de creación:' : 'Cambios realizados:'}
                          </h6>
                          {renderChanges(log)}
                          
                          {log.ipAddress && (
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-muted">
                                IP: {log.ipAddress}
                              </small>
                            </div>
                          )}
                        </div>
                      </Collapse>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          Cerrar
        </button>
        <button className="btn btn-primary" onClick={refetch} disabled={loading}>
          <FontAwesomeIcon icon={faHistory} className="me-2" />
          Actualizar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default HistoryModal; 